// ============================================================================
// AssignDoctorDialog.tsx — КОМАНДА «назначить/передать лечащего врача».
//
// Один серверный вызов (MedicService.AssignAttendingDoctor): явная связь
// пациента и история команды меняются в одной транзакции — руками две таблицы
// не правятся. Врачи — люди с ролью «Врач» (если роль настроена), иначе все
// пользователи; поиск по ФИО/логину.
// ============================================================================

import { useEffect, useMemo, useState } from 'react';
import { Loader2, Search, Stethoscope } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { UniversalWindow } from '@/components/ui/UniversalWindow';
import { friendlyError, logErrorToJournal } from '@/lib/errors';
import { getSDK } from '@/store/sdkStore';

interface DoctorOption {
    id: string;
    name: string;
    login: string;
}

interface Props {
    patientId: string;
    patientName?: string;
    currentDoctorId?: string;
    onClose: () => void;
    onAssigned: () => void;
}

export function AssignDoctorDialog({ patientId, patientName, currentDoctorId, onClose, onAssigned }: Props) {
    const [doctors, setDoctors] = useState<DoctorOption[]>([]);
    const [filteredByRole, setFilteredByRole] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [query, setQuery] = useState('');
    const [selectedId, setSelectedId] = useState('');
    const [reason, setReason] = useState('');
    const [saving, setSaving] = useState(false);
    const [resultNote, setResultNote] = useState('');

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            setError('');
            try {
                const sdk = getSDK();
                let roleId = '';
                try {
                    const roles = await sdk.auth.getRoles({});
                    const doctorRole = (roles.items || []).find(
                        (role) => String(role.name || '').trim().toLowerCase() === 'врач',
                    );
                    roleId = doctorRole?.id || '';
                } catch (rolesError) {
                    // Роли недоступны — показываем всех людей (не подменяем ошибку).
                    logErrorToJournal(rolesError, 'Лечащий врач: список ролей');
                }
                const list = await sdk.entity.listEntities({ roleId, peopleOnly: true });
                if (cancelled) return;
                const items = (list.items || [])
                    .map((entity) => ({
                        id: String(entity.id || ''),
                        name: String(entity.name || entity.login || 'Без имени'),
                        login: String(entity.login || ''),
                    }))
                    .filter((item) => item.id !== '')
                    .sort((a, b) => a.name.localeCompare(b.name, 'ru'));
                setDoctors(items);
                setFilteredByRole(!!roleId);
                setSelectedId(
                    items.some((item) => item.id === currentDoctorId)
                        ? String(currentDoctorId)
                        : '',
                );
            } catch (loadError) {
                if (cancelled) return;
                logErrorToJournal(loadError, 'Лечащий врач: список пользователей');
                setError(friendlyError(loadError));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [currentDoctorId]);

    const visible = useMemo(() => {
        const needle = query.trim().toLowerCase();
        if (!needle) return doctors;
        return doctors.filter((doctor) =>
            doctor.name.toLowerCase().includes(needle) || doctor.login.toLowerCase().includes(needle),
        );
    }, [doctors, query]);

    const assign = async () => {
        if (!selectedId || saving) return;
        setSaving(true);
        setError('');
        setResultNote('');
        try {
            const response = await getSDK().medic.assignAttendingDoctor({
                patientId,
                doctorId: selectedId,
                reason: reason.trim(),
            });
            if (!response.changed) {
                setResultNote('Этот врач уже указан лечащим — история не менялась.');
                setSaving(false);
                return;
            }
            onAssigned();
            onClose();
        } catch (saveError) {
            logErrorToJournal(saveError, `Лечащий врач пациента ${patientId}: назначение`);
            setError(friendlyError(saveError));
        } finally {
            setSaving(false);
        }
    };

    return (
        <UniversalWindow
            open
            onClose={onClose}
            title={currentDoctorId ? 'Передать пациента врачу' : 'Назначить лечащего врача'}
            width="w-[min(560px,94vw)]"
            resizable={false}
        >
            <div className="flex h-[min(64vh,560px)] flex-col">
                <div className="shrink-0 space-y-2 border-b p-3">
                    <div className="text-xs text-muted-foreground">
                        Пациент: <span className="text-foreground">{patientName || patientId}</span>
                    </div>
                    <div className="relative">
                        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            value={query}
                            onChange={(event) => setQuery(event.target.value)}
                            placeholder="Поиск врача по ФИО или логину"
                            className="h-9 pl-8"
                            autoComplete="off"
                        />
                    </div>
                    <div className="text-[11px] text-muted-foreground">
                        {filteredByRole
                            ? 'Показаны пользователи с ролью «Врач».'
                            : 'Роль «Врач» в базе не заведена — показаны все пользователи.'}
                    </div>
                </div>

                <div className="min-h-0 flex-1 space-y-1 overflow-y-auto p-3">
                    {loading && (
                        <div className="flex items-center gap-2 rounded-md border px-3 py-2 text-xs text-muted-foreground">
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            Загружаем список врачей…
                        </div>
                    )}
                    {!loading && error && (
                        <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-destructive">
                            {error}
                        </div>
                    )}
                    {!loading && !error && visible.length === 0 && (
                        <div className="rounded-md border px-3 py-2 text-xs text-muted-foreground">
                            {doctors.length === 0
                                ? 'Нет доступных пользователей для назначения.'
                                : 'По запросу никого не найдено.'}
                        </div>
                    )}
                    {visible.map((doctor) => (
                        <button
                            key={doctor.id}
                            type="button"
                            onClick={() => setSelectedId(doctor.id)}
                            className={`flex w-full items-center gap-3 rounded-md border px-3 py-2 text-left text-sm transition-colors ${
                                selectedId === doctor.id
                                    ? 'border-primary bg-primary/5'
                                    : 'border-transparent hover:bg-accent/20'
                            }`}
                        >
                            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border bg-primary/10 text-primary">
                                <Stethoscope className="h-4 w-4" />
                            </span>
                            <span className="min-w-0 flex-1">
                                <span className="block truncate font-medium">{doctor.name}</span>
                                {doctor.login && (
                                    <span className="block truncate text-[11px] text-muted-foreground">
                                        {doctor.login}
                                    </span>
                                )}
                            </span>
                            {selectedId === doctor.id && (
                                <span className="rounded-full bg-primary px-2 py-0.5 text-[10px] text-primary-foreground">
                                    выбран
                                </span>
                            )}
                        </button>
                    ))}
                </div>

                <div className="shrink-0 space-y-2 border-t p-3">
                    <Input
                        value={reason}
                        onChange={(event) => setReason(event.target.value)}
                        placeholder="Причина передачи (необязательно, сохранится в истории)"
                        className="h-9"
                    />
                    {resultNote && (
                        <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-2.5 py-1.5 text-[11px] text-amber-700">
                            {resultNote}
                        </div>
                    )}
                    <div className="flex justify-end gap-2">
                        <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={saving}>
                            Отмена
                        </Button>
                        <Button
                            type="button"
                            size="sm"
                            onClick={() => void assign()}
                            disabled={!selectedId || saving || loading}
                        >
                            {saving && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
                            {currentDoctorId ? 'Передать' : 'Назначить'}
                        </Button>
                    </div>
                </div>
            </div>
        </UniversalWindow>
    );
}
